import React, { useCallback } from 'react';

import validate from '@vlabs/shared/validators/index.js';
import { Button } from '@vlabs/uikit/components/controls/button';
import { Checkbox } from '@vlabs/uikit/components/controls/checkbox';
import type { TOption } from '@vlabs/uikit/types';
import { useForm } from 'react-hook-form';

import { getDepartments } from '@vlabs/api/artemy/client';
import type { DepartmentId } from '@vlabs/api/artemy/model';
import { ControllableValueSelect } from '@vlabs/monokit/shared/ui/value-select';

import st from './MoveListsToDepartmentForm.module.sass';
import { useMoveListsToDepartmentTranslation } from '../i18n';
import type { MoveListsToDepartmentFormValues } from '../types';

interface MoveListsToDepartmentFormProps {
  departmentId: DepartmentId;
  onCancel: VoidFunction;
  onSubmit: (values: MoveListsToDepartmentFormValues) => Promise<void>;
}

function MoveListsToDepartmentForm({
  departmentId,
  onCancel,
  onSubmit,
}: MoveListsToDepartmentFormProps): React.JSX.Element {
  const { t } = useMoveListsToDepartmentTranslation();
  const form = useForm<MoveListsToDepartmentFormValues>({
    defaultValues: {
      targetDepartmentId: undefined,
      keepViewPermission: true,
    },
  });

  const loadOptions = useCallback(
    async (inputValue: string): Promise<TOption[]> => {
      const { departments = [] } = await getDepartments({ ...(inputValue ? { name: inputValue } : {}) });
      return departments
        .map((department) => ({
          value: department.department_id,
          label: department.name ?? department.department_id,
        })).filter((option) => option.value !== departmentId);
    },
    [departmentId],
  );

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <div className={st.MoveListsToDepartment}>
        <div className={st.Header}>
          <h5>{t('title')}</h5>
          <span className={st.Description}>{t('description')}</span>
        </div>

        <div className={st.Fields}>
          <div>
            <ControllableValueSelect
              cacheOptions
              control={form.control}
              defaultOptions
              errors={form.formState.errors}
              id="targetDepartmentId"
              isAsync
              isClearable
              label={t('fields.department')}
              loadOptions={loadOptions}
              name="targetDepartmentId"
              noOptionsMessage={() => t('fields.department_empty')}
              placeholder={t('fields.department_placeholder')}
              rules={{ required: validate.required() }}
            />
          </div>

          <div>
            <Checkbox
              {...form.register('keepViewPermission')}
              id="keepViewPermission"
              label={t('fields.keep_view_permission')}
              name="keepViewPermission"
            />
          </div>
        </div>

        <div className={st.Actions}>
          <Button
            className={st.Button}
            disabled={form.formState.isSubmitting}
            isLoading={form.formState.isSubmitting}
            type="submit"
          >
            {t('submit')}
          </Button>

          <Button
            className={st.Button}
            kind="secondary"
            onClick={onCancel}
            type="button"
            variant="outlined"
          >
            {t('cancel')}
          </Button>
        </div>
      </div>
    </form>
  );
}

export { MoveListsToDepartmentForm };
